import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Inbox, ArrowLeft, Loader2, Trash2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("et_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [insights, setInsights] = useState([]);
  const [nTitle, setNTitle] = useState("");
  const [nTag, setNTag] = useState("");
  const [nBody, setNBody] = useState("");
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async (tok) => {
    setFetching(true);
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, { headers: { Authorization: `Bearer ${tok}` } });
      setEnquiries(data);
      const ins = await axios.get(`${API}/insights`);
      setInsights(ins.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("et_admin_token");
        setToken("");
      } else {
        toast.error("Failed to load enquiries");
      }
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      localStorage.setItem("et_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("et_admin_token");
    setToken("");
    setEnquiries([]);
  };

  const publish = async (e) => {
    e.preventDefault();
    setPublishing(true);
    try {
      await axios.post(
        `${API}/admin/insights`,
        { title: nTitle, body: nBody, tag: nTag || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNTitle("");
      setNTag("");
      setNBody("");
      toast.success("Insight published");
      load(token);
    } catch {
      toast.error("Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const removeInsight = async (id) => {
    try {
      await axios.delete(`${API}/admin/insights/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      load(token);
    } catch {
      toast.error("Delete failed");
    }
  };

  if (!token) {
    return (
      <div className="grid min-h-screen place-items-center bg-paper px-5" data-testid="admin-login-page">
        <div className="w-full max-w-md rounded-[1.75rem] border border-neutral-200 bg-white p-9 shadow-[0_30px_70px_-40px_rgba(44,56,28,0.35)]">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand-600 text-white"><Lock size={19} /></span>
            <div>
              <h1 className="font-display text-xl font-extrabold text-neutral-950">Admin Portal</h1>
              <p className="font-mono text-[11px] tracking-[0.2em] text-neutral-400">ELECTRO.TECHNOLOGY</p>
            </div>
          </div>
          <form onSubmit={login} className="mt-8 space-y-4">
            <input
              data-testid="admin-login-email-input"
              type="email" required placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border border-neutral-200 px-4 py-3 text-sm outline-none transition-colors focus:border-brand-600"
            />
            <input
              data-testid="admin-login-password-input"
              type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-xl border border-neutral-200 px-4 py-3 text-sm outline-none transition-colors focus:border-brand-600"
            />
            {error && <p data-testid="admin-login-error" className="text-sm font-medium text-red-600">{error}</p>}
            <button
              type="submit" data-testid="admin-login-submit-button" disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-600 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-60"
            >
              {loading && <Loader2 size={15} className="animate-spin" />} Sign in
            </button>
          </form>
          <Link to="/" data-testid="admin-back-home-link" className="mt-6 inline-flex items-center gap-1.5 text-xs text-neutral-500 transition-colors hover:text-brand-700">
            <ArrowLeft size={13} /> Back to site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
      <div className="mx-auto max-w-6xl px-5 py-10 lg:px-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-extrabold text-neutral-950 sm:text-3xl">Fleet enquiries</h1>
            <p className="mt-1 font-mono text-[11px] tracking-[0.2em] text-neutral-400">
              <Inbox size={11} className="mr-1 inline" /> {enquiries.length} TOTAL
            </p>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="admin-refresh-button" onClick={() => load(token)}
              className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-5 py-2.5 text-xs font-semibold text-neutral-700 transition-colors hover:border-brand-600 hover:text-brand-700"
            >
              <RefreshCw size={13} className={fetching ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              data-testid="admin-logout-button" onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 text-xs font-semibold text-white transition-colors hover:bg-brand-700"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>

        <div className="mt-8 space-y-4" data-testid="admin-enquiries-list">
          {enquiries.length === 0 && !fetching && (
            <div className="rounded-2xl border border-dashed border-neutral-300 bg-white p-12 text-center text-sm text-neutral-400" data-testid="admin-enquiries-empty">
              No enquiries yet.
            </div>
          )}
          {enquiries.map((q) => (
            <article key={q.id} data-testid={`admin-enquiry-${q.id}`} className="rounded-2xl border border-neutral-200 bg-white p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="font-display text-lg font-bold text-neutral-900">{q.name}{q.company ? <span className="text-neutral-400"> · {q.company}</span> : null}</h2>
                <span className="font-mono text-[11px] text-neutral-400">{q.created_at ? new Date(q.created_at).toLocaleString() : ""}</span>
              </div>
              <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-1 text-xs text-neutral-500">
                <a href={`mailto:${q.email}`} className="text-brand-700 underline decoration-brand-200 underline-offset-2">{q.email}</a>
                {q.phone && <span>{q.phone}</span>}
                {q.fleet_size && <span className="rounded-full bg-brand-100 px-2.5 py-0.5 font-medium text-brand-700">{q.fleet_size}</span>}
                {q.vehicle_interest && <span className="rounded-full bg-neutral-100 px-2.5 py-0.5 font-medium text-neutral-600">{q.vehicle_interest}</span>}
              </div>
              <p className="mt-3 text-sm leading-relaxed text-neutral-600">{q.message}</p>
            </article>
          ))}
        </div>

        <div className="mt-14 border-t border-neutral-200 pt-10" data-testid="insights-manager">
          <h2 className="font-display text-xl font-extrabold text-neutral-950 sm:text-2xl">Expert insights</h2>
          <p className="mt-1 font-mono text-[11px] tracking-[0.15em] text-neutral-400">
            PUBLISHED AS GREGORY RADEK · SITE FEED + /api/insights + /api/insights.md
          </p>
          <form onSubmit={publish} className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6">
            <div className="grid gap-4 sm:grid-cols-[1fr_200px]">
              <input data-testid="insight-title-input" required maxLength={160} placeholder="Title" value={nTitle} onChange={(e) => setNTitle(e.target.value)} className="w-full rounded-xl border border-neutral-200 px-4 py-3 text-sm outline-none transition-colors focus:border-brand-600" />
              <input data-testid="insight-tag-input" maxLength={40} placeholder="Tag (e.g. Africa)" value={nTag} onChange={(e) => setNTag(e.target.value)} className="w-full rounded-xl border border-neutral-200 px-4 py-3 text-sm outline-none transition-colors focus:border-brand-600" />
            </div>
            <textarea data-testid="insight-body-input" required maxLength={2000} rows={3} placeholder="Short EV-market note…" value={nBody} onChange={(e) => setNBody(e.target.value)} className="w-full rounded-xl border border-neutral-200 px-4 py-3 text-sm outline-none transition-colors focus:border-brand-600 mt-4 resize-none" />
            <button type="submit" data-testid="insight-publish-button" disabled={publishing} className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-60">
              {publishing ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              {publishing ? "Publishing…" : "Publish insight"}
            </button>
          </form>
          <div className="mt-6 space-y-3" data-testid="admin-insights-list">
            {insights.map((n) => (
              <article key={n.id} data-testid={`admin-insight-${n.id}`} className="flex items-start justify-between gap-4 rounded-2xl border border-neutral-200 bg-white p-5">
                <div className="min-w-0">
                  <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-400">
                    {n.tag ? `${n.tag} · ` : ""}{n.created_at ? new Date(n.created_at).toLocaleDateString() : ""}
                  </div>
                  <h3 className="mt-1 font-display font-bold text-neutral-900">{n.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-neutral-500">{n.body}</p>
                </div>
                <button data-testid={`insight-delete-${n.id}`} onClick={() => removeInsight(n.id)} aria-label="Delete insight" className="shrink-0 rounded-full border border-neutral-200 p-2 text-neutral-400 transition-colors hover:border-red-300 hover:text-red-600">
                  <Trash2 size={14} />
                </button>
              </article>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
